import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { uploadManyImos as uploadMany, fileUrl } from "@/lib/uploads";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, Upload, Check, ClipboardCheck, Layers, PenTool, Presentation,
} from "lucide-react";

/**
 * ImosSignoffPage — Feb 2026
 * Landing page for Interior Designers and Design Managers to complete the
 * IMOS Sign-off stage. Shows orders in `sign_off` status assigned to them,
 * lets them upload 4 files (2D / 3D / PPT / Sign-off Sheet) and mark complete.
 * Once complete, the IMOS Executive receives a pending action notification.
 */
const SLOTS = [
  { key: "two_d",         label: "2D Drawings",   icon: Layers },
  { key: "three_d",       label: "3D Views",      icon: PenTool },
  { key: "ppt",           label: "Presentation",  icon: Presentation },
  { key: "signoff_sheet", label: "Sign-off Sheet", icon: ClipboardCheck },
];

export default function ImosSignoffPage() {
  const { principal } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/imos/signoff-queue");
      setOrders(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);
  const selected = orders.find((o) => o.id === selectedId);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design · IMOS Sign-off</p>
        <h1 className="font-display text-3xl text-white mt-1">Sign-off Queue</h1>
        <p className="text-xs text-gray-500 mt-1">Upload 2D · 3D · PPT · Sign-off Sheet, then mark complete to hand back to IMOS.</p>
      </div>

      <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 items-start">
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Awaiting Sign-off</p>
          {loading && <p className="text-xs text-gray-500">Loading…</p>}
          {!loading && orders.length === 0 && (
            <div className="iz-card p-6 text-center" data-testid="signoff-empty">
              <p className="text-xs text-gray-500">Nothing awaiting sign-off.</p>
            </div>
          )}
          {orders.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              data-testid={`signoff-row-${o.id}`}
              className={`iz-card p-3 w-full text-left transition-colors hover:border-[#d4af37]/40 ${selectedId === o.id ? "border-[#d4af37]" : ""}`}
            >
              <p className="text-white text-sm truncate">{o.client_name} · {o.city}</p>
              <p className="text-[11px] text-gray-500 truncate">{o.project_name}</p>
              {o.signoff_complete && (
                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mt-1">✓ Complete</p>
              )}
            </button>
          ))}
        </div>

        <div className="min-w-0">
          {selected ? (
            <SignoffDetail order={selected} onRefresh={load} />
          ) : (
            <div className="iz-card p-12 text-center">
              <FileText className="w-8 h-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Select an order to upload sign-off files.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SignoffDetail({ order, onRefresh }) {
  const [marking, setMarking] = useState(false);
  const files = order.signoff_files || {};

  const doUpload = async (slotKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const uploaded = await uploadMany([file]);
      await api.post(`/imos/orders/${order.id}/signoff-files`, {
        [`${slotKey}_file_id`]: uploaded[0].id,
      });
      toast.success(`${slotKey.replace('_', ' ').toUpperCase()} uploaded`);
      onRefresh?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const markComplete = async () => {
    setMarking(true);
    try {
      await api.post(`/imos/orders/${order.id}/signoff-complete`);
      toast.success("Sign-off marked complete · IMOS Executive notified");
      onRefresh?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setMarking(false); }
  };

  const missing = SLOTS.filter((s) => !files[s.key]);
  return (
    <div className="iz-card p-6 space-y-4" data-testid="signoff-detail">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">IMOS · Sign-off</p>
        <h2 className="font-display text-2xl text-white mt-1">{order.client_name}{order.city ? ` · ${order.city}` : ""}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{order.project_name}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SLOTS.map((s) => {
          const Icon = s.icon;
          const f = files[s.key];
          return (
            <div key={s.key} className={`border rounded p-3 ${f ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-[#262626]"}`} data-testid={`signoff-slot-${s.key}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 text-[#d4af37]" strokeWidth={1.5} />
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400">{s.label}</p>
              </div>
              {f ? (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-300 truncate">{f.original_filename}</span>
                  <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" className="text-[#d4af37] font-mono text-[10px] uppercase tracking-widest ml-2">Open</a>
                </div>
              ) : (
                <p className="text-[11px] text-gray-600 italic mt-1">Not uploaded</p>
              )}
              {!order.signoff_complete && (
                <label className="iz-btn-secondary text-xs mt-2 flex items-center justify-center gap-1.5 cursor-pointer" data-testid={`signoff-upload-${s.key}`}>
                  <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {f ? "Replace" : "Upload"}
                  <input type="file" onChange={(e) => doUpload(s.key, e)} className="hidden" />
                </label>
              )}
            </div>
          );
        })}
      </div>

      {order.signoff_complete ? (
        <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-md px-4 py-3 flex items-center gap-2" data-testid="signoff-complete-badge">
          <Check className="w-4 h-4 text-emerald-400" />
          <p className="text-emerald-400 text-xs font-mono uppercase tracking-widest">
            Sign-off complete · IMOS Executive notified
          </p>
        </div>
      ) : (
        <button
          onClick={markComplete}
          disabled={marking || missing.length > 0}
          className="iz-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40"
          data-testid="signoff-complete-btn"
        >
          <Check className="w-3.5 h-3.5" />
          {marking ? "Marking…" :
            missing.length > 0 ? `Upload ${missing.length} more file(s) to enable` : "Mark Sign-off Complete"}
        </button>
      )}
    </div>
  );
}
